import * as React from 'react';
import { styled } from '@glitz/react';
import { logError } from './logging';
import { type AsyncObservable, type SyncObservable } from './events';

export type ErrorProps = {
  error: Error | undefined;
};

type StateType = { hasError: boolean; error: Error | undefined };

const defaultState = { hasError: false, error: undefined };

export function createErrorBoundary<TProps>(
  Component: React.ComponentType<TProps & {}>,
  Fallback: React.ComponentType<ErrorProps> = DefaultError,
  observables?: (SyncObservable<any> | AsyncObservable<any>)[],
): React.FunctionComponent<TProps & {}> {
  class ErrorBoundary extends React.Component<React.PropsWithChildren, StateType> {
    unobserves: (() => void)[] = [];
    static getDerivedStateFromError(error: Error): StateType {
      return { hasError: true, error };
    }
    state = defaultState;
    componentDidCatch(error: Error, info: React.ErrorInfo) {
      logError(error);
    }
    componentDidMount() {
      if (observables) {
        for (const observable of observables) {
          this.unobserves.push(
            observable.on(() => {
              // Wait for all observables to emit before retrying
              requestAnimationFrame(() => {
                if (this.state.hasError) {
                  this.setState(defaultState);
                }
              });
            }),
          );
        }
      }
    }
    componentWillUnmount() {
      for (const unobserve of this.unobserves) {
        unobserve();
      }
      this.unobserves = [];
    }
    render() {
      if (this.state.hasError) {
        return <Fallback error={this.state.error} />;
      }

      return this.props.children;
    }
  }

  return (props: TProps & {}) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
}

const DefaultError = styled((props: ErrorProps) => {
  return __DEV__ ? (
    <styled.Pre
      css={{
        margin: { xy: 0 },
        padding: { xy: '1em' },
        textAlign: 'center',
        fontSize: '1rem',
        backgroundColor: 'red',
        color: 'white',
      }}
    >
      💥 Error: {props.error?.message ?? 'Unknown'}
    </styled.Pre>
  ) : null;
});
